// v2/components/PagoReturnV2.jsx
//
// Página de retorno tras pago Webpay. Webpay redirige aquí con `token_ws`
// y nosotros llamamos al endpoint idempotente del backend.
//
// Estados posibles:
//  - aprobado: muestra confirmación + lista de inscripciones
//  - aprobado + parcial: el pago se cobró pero falta gente por inscribir. Se
//    nombra a quién falta y se le dice que NO vuelva a pagar. Sin esta rama, una
//    compra de dos que inscribe a uno mostraba "¡Inscripción confirmada!" y el
//    socio volvía a comprar el cupo que ya había pagado (los cobros duplicados de
//    julio-2026 salieron de ahí).
//  - rechazado: muestra motivo + botón volver al carrito
//  - requiere_revision: presencial y pagos viejos; el online ya no cae acá
//  - IDEM_EN_CURSO: el hook hace polling automático

import React, { useEffect } from 'react';
import { Card, Result, Spin, Alert, Button, Typography, Tag, Space } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, WarningOutlined,
  LoadingOutlined, HomeOutlined, ShoppingCartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import usePagoWebpay from '../../../hooks/usePagoWebpay';
import { useCart } from '../../../providers/Cart';

const { Title, Text, Paragraph } = Typography;

/**
 * @param {object} props
 * @param {'online'|'venta_presencial'} [props.flujo='online']
 */
export default function PagoReturnV2({ flujo = 'online' }) {
  const navigate = useNavigate();
  const { loading, resultado, error } = usePagoWebpay({ flujo });
  const { refreshCart } = useCart();

  // Si el cobro salió, el backend ya cerró el carrito que originó la compra; acá se
  // relee para que el contador del header no siga mostrando los ítems ya pagados.
  // Sin esto el socio ve "3" en el carrito después de pagar, entra, y encuentra todo
  // otra vez: la invitación perfecta a pagar dos veces lo mismo.
  const cobroSalio = resultado && ['aprobado', 'completada', 'parcial', 'requiere_revision'].includes(resultado.estado);
  useEffect(() => {
    if (cobroSalio) refreshCart().catch(() => {});
    // `refreshCart` se omite a propósito: cambia de identidad en cada render del
    // provider y volvería a disparar el efecto en loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobroSalio]);

  if (loading) {
    return (
      <Centrado>
        <Card style={{ textAlign: 'center', minWidth: 360 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} size="large" />
          <Title level={3} style={{ marginTop: 24 }}>Confirmando tu pago…</Title>
          <Paragraph type="secondary">
            Estamos verificando tu transacción con Webpay. No cierres esta ventana.
          </Paragraph>
        </Card>
      </Centrado>
    );
  }

  if (error) {
    return (
      <Centrado>
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title="No pudimos confirmar tu pago"
          subTitle={error.mensaje}
          extra={[
            <Button key="carrito" type="primary" onClick={() => navigate('/carrito')}>
              Volver al carrito
            </Button>,
            <Button key="home" onClick={() => navigate('/')}>Inicio</Button>
          ]}
        />
      </Centrado>
    );
  }

  if (!resultado) return null;

  if (resultado.estado === 'aprobado' || resultado.estado === 'completada') {
    const codigo = resultado.numeroOrden || resultado.codigoVenta;
    const inscripciones = resultado.inscripcionIds || [];
    const pendientes = resultado.itemsPendientes || [];
    const esParcial = resultado.parcial === true || pendientes.length > 0;

    // El pago se cobró, pero no todos los inscritos quedaron. Decirle "¡Inscripción
    // confirmada!" acá sería mentirle: se iría creyendo que su hijo quedó, y cuando
    // se entere volvería a comprar el cupo que ya pagó. Eso es exactamente lo que
    // generó los cobros duplicados de julio.
    if (esParcial) {
      const total = inscripciones.length + pendientes.length;
      return (
        <Centrado>
          <Card style={{ maxWidth: 640 }}>
            <Result
              status="warning"
              icon={<WarningOutlined />}
              title={pendientes.length === 1
                ? 'Tu pago se realizó, pero falta una inscripción'
                : `Tu pago se realizó, pero faltan ${pendientes.length} inscripciones`}
              subTitle={
                <Space direction="vertical" align="center">
                  <Tag color="blue">Pago aprobado</Tag>
                  {codigo && <Text type="secondary">Código: <Text code>{codigo}</Text></Text>}
                </Space>
              }
            />
            <Alert
              type="warning"
              showIcon
              message={`Quedaron ${inscripciones.length} de ${total} inscripciones`}
              description={
                <>
                  <Paragraph style={{ marginBottom: 4 }}>No pudimos completar la de:</Paragraph>
                  <ListaPendientes pendientes={pendientes} />
                  <Paragraph style={{ marginBottom: 0 }}>
                    <Text strong>No tienes que volver a pagar.</Text> Ya estamos al tanto y te
                    contactamos dentro de las próximas 24 horas para dejarlo resuelto.
                    {codigo && <> Si prefieres escribir a soporte, menciona el código <Text code>{codigo}</Text>.</>}
                  </Paragraph>
                </>
              }
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <Button type="primary" block size="large" onClick={() => navigate('/mis-talleres')}>
                Ver lo que sí quedó inscrito
              </Button>
              <Button block size="large" icon={<HomeOutlined />} onClick={() => navigate('/')}>
                Inicio
              </Button>
            </div>
          </Card>
        </Centrado>
      );
    }

    return (
      <Centrado>
        <Card style={{ maxWidth: 640 }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#0047bb' }} />}
            title="¡Inscripción confirmada!"
            subTitle={
              <Space direction="vertical" align="center">
                <Tag color="blue">Pago aprobado</Tag>
                {codigo && <Text type="secondary">Código: <Text code>{codigo}</Text></Text>}
              </Space>
            }
          />
          {inscripciones.length > 0 && (
            <Alert
              type="success" showIcon
              message={`Se crearon ${inscripciones.length} inscripción(es)`}
              description="La boleta llegará a tu email en los próximos minutos. Si no aparece, revisa tu carpeta de spam."
              style={{ marginBottom: 16 }}
            />
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <Button type="primary" block size="large" onClick={() => navigate('/mis-talleres')}>
              Ver mis inscripciones
            </Button>
            <Button block size="large" icon={<HomeOutlined />} onClick={() => navigate('/')}>
              Inicio
            </Button>
          </div>
        </Card>
      </Centrado>
    );
  }

  // Venta presencial que ya no estaba en curso (cancelada, o cobrada por otro
  // medio): el backend no confirma el cobro y Transbank reversa la autorización.
  if (resultado.estado === 'no_cobrado') {
    return (
      <Centrado>
        <Result
          status="info"
          title="Este pago no se realizó"
          subTitle={resultado.motivo || 'La venta ya no estaba activa.'}
          extra={[
            <Paragraph key="aviso" type="secondary" style={{ maxWidth: 420, margin: '0 auto' }}>
              No se te cobrará. Si tu banco muestra una retención por este monto, se libera
              sola en unos días. Consulta con la persona que te atendió en el recinto.
            </Paragraph>
          ]}
        />
      </Centrado>
    );
  }

  if (resultado.estado === 'rechazado' || resultado.estado === 'rechazada') {
    return (
      <Centrado>
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title="Tu pago fue rechazado"
          subTitle={resultado.motivo || 'Webpay rechazó la transacción.'}
          extra={[
            /* Antes el único texto era "intentar de nuevo", sin más. Un rechazo de
               Webpay casi nunca cobra, pero cuando sí llegó a cobrar (el caso que
               estuvimos remediando), reintentar a ciegas termina en dos cargos. Se
               pide revisar la cartola primero: cuesta un minuto y evita un reembolso. */
            <Paragraph key="aviso" type="secondary" style={{ maxWidth: 420, margin: '0 auto 16px' }}>
              Si en tu cartola aparece el cargo, <Text strong>no vuelvas a pagar</Text>:
              escríbenos y lo revisamos. Un rechazo casi nunca descuenta, pero si
              descontó, pagar de nuevo te cobraría dos veces.
            </Paragraph>,
            <Button key="carrito" type="primary" icon={<ShoppingCartOutlined />} onClick={() => navigate('/carrito')}>
              Volver al carrito e intentar de nuevo
            </Button>
          ]}
        />
      </Centrado>
    );
  }

  if (resultado.estado === 'requiere_revision') {
    const inscripciones = resultado.inscripcionIds || [];
    const codigo = resultado.numeroOrden || resultado.codigoVenta;
    // El flujo online ya no llega acá (queda 'aprobado' + parcial), pero el
    // presencial y los pagos viejos sí. Puede traer el detalle en la raíz o dentro
    // de `detalles`, según el flujo.
    const pendientes = resultado.itemsPendientes || resultado.detalles?.itemsPendientes || [];
    return (
      <Centrado>
        <Card style={{ maxWidth: 640 }}>
          <Result
            status="warning"
            icon={<WarningOutlined />}
            title="Tu pago se realizó, pero quedó algo por resolver"
            subTitle={
              <Space direction="vertical" align="center">
                {codigo && <Text type="secondary">Código: <Text code>{codigo}</Text></Text>}
                <Text>
                  Ya estamos al tanto y te contactamos dentro de las próximas 24 horas.
                </Text>
              </Space>
            }
          />
          <Alert
            type="warning"
            showIcon
            message={inscripciones.length > 0
              ? `Quedaron ${inscripciones.length} inscripción(es) y falta completar el resto`
              : 'Tu inscripción quedó pendiente de completarse'}
            description={
              <>
                {pendientes.length > 0 && (
                  <>
                    <Paragraph style={{ marginBottom: 4 }}>Falta la de:</Paragraph>
                    <ListaPendientes pendientes={pendientes} />
                  </>
                )}
                <Paragraph style={{ marginBottom: 0 }}>
                  <Text strong>No tienes que volver a pagar.</Text>
                  {codigo && <> Si escribes a soporte, menciona el código <Text code>{codigo}</Text>.</>}
                </Paragraph>
                {resultado.motivo && (
                  <Text type="secondary" style={{ fontSize: 12 }}>{resultado.motivo}</Text>
                )}
              </>
            }
            style={{ marginBottom: 16 }}
          />
          <div style={{ display: 'flex', gap: 12 }}>
            {inscripciones.length > 0 && (
              <Button type="primary" block size="large" onClick={() => navigate('/mis-talleres')}>
                Ver lo que sí quedó inscrito
              </Button>
            )}
            <Button block size="large" icon={<HomeOutlined />} onClick={() => navigate('/')}>
              Inicio
            </Button>
          </div>
        </Card>
      </Centrado>
    );
  }

  // Caso desconocido
  return (
    <Centrado>
      <Result
        title="Estado de pago no reconocido"
        subTitle={`Estado: ${resultado.estado}`}
        extra={[<Button key="home" onClick={() => navigate('/')}>Volver al inicio</Button>]}
      />
    </Centrado>
  );
}

// Quién quedó sin inscribir, con nombre. El conteo solo ("se generaron 1
// inscripción(es)") dejaba al socio sin saber si el que quedó era él o su hijo.
function ListaPendientes({ pendientes = [] }) {
  if (pendientes.length === 0) return null;
  return (
    <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
      {pendientes.map((p, i) => (
        <li key={p.rut || p.nombre || i}>
          <Text strong>{p.nombre || 'Inscrito sin nombre'}</Text>
          {p.taller && <Text type="secondary"> — {p.taller}</Text>}
        </li>
      ))}
    </ul>
  );
}

function Centrado({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16
    }}>
      {children}
    </div>
  );
}
